/**
 * Package Builder — assets/package-builder.js
 * Compatible with: Broadcast 5.1.3 / Shopify Basic plan
 * Built by: Devsinside Ltd
 *
 * Cart strategy: multi-item POST /cart/add.js → redirect /cart
 * All bundle items share a _package_id line property for grouping
 * in Shopify's cart, order confirmation, and admin.
 */

class PackageBuilder {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.moneyFormat = container.dataset.moneyFormat || '${{amount}}';

    // Read step config injected by Liquid
    const configEl = document.getElementById(`PbConfig-${this.sectionId}`);
    if (!configEl) return;
    this.config = JSON.parse(configEl.textContent);
    this.steps = this.config.steps;
    this.totalSteps = this.steps.length;

    // State: { [stepIndex]: [{ variantId, productId, price, name }] }
    this.selections = {};
    this.currentStep = 0;

    // Bundle discount config (assigned in theme editor)
    this.bundle = (this.config.bundle && this.config.bundle.enabled) ? this.config.bundle : null;
    this.bundleQualified = false;

    // DOM refs
    this.stepPanels    = [...container.querySelectorAll('.pb-step')];
    this.stepperItems  = [...container.querySelectorAll('.pb-stepper__item')];
    this.summaryList   = container.querySelector(`#PbSummaryList-${this.sectionId}`);
    this.summaryFooter = container.querySelector(`#PbSummaryFooter-${this.sectionId}`);
    this.totalPriceEl  = container.querySelector(`#PbTotalPrice-${this.sectionId}`);
    this.sidebarBtn    = container.querySelector(`#PbSidebarCartBtn-${this.sectionId}`);
    this.sidebarNote   = container.querySelector(`#PbSidebarNote-${this.sectionId}`);
    this.loadingEl     = container.querySelector(`#PbLoading-${this.sectionId}`);
    this.bundleBanner  = container.querySelector(`#PbBundleBanner-${this.sectionId}`);

    this._bindEvents();
    this._updateNextBtn(); // set initial disabled state on required steps
  }

  // ─── Event binding ──────────────────────────────────────────

  _bindEvents() {
    // Product card clicks (keyboard + mouse)
    this.container.querySelectorAll('.pb-product').forEach(card => {
      card.addEventListener('click', e => {
        if (e.target.closest('[data-product-card-variant]')) return; // let select handle it
        this._handleProductClick(card);
      });
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._handleProductClick(card);
        }
      });
    });

    // Variant selects — update price and variant ID on selected card
    this.container.querySelectorAll('[data-product-card-variant]').forEach(select => {
      select.addEventListener('change', () => {
        const card = select.closest('.pb-product');
        if (card.classList.contains('is-selected')) {
          this._updateCardVariant(card, select);
        }
      });
      // Stop card click bubbling when interacting with select
      select.addEventListener('click', e => e.stopPropagation());
    });

    // Step navigation buttons
    this.container.querySelectorAll('[data-action="prev"]').forEach(btn => {
      btn.addEventListener('click', () => this._goPrev());
    });
    this.container.querySelectorAll('[data-action="next"]').forEach(btn => {
      btn.addEventListener('click', () => this._goNext());
    });
    this.container.querySelectorAll('[data-action="add-to-cart"]').forEach(btn => {
      btn.addEventListener('click', () => this._addToCart());
    });

    // Sidebar cart button
    if (this.sidebarBtn) {
      this.sidebarBtn.addEventListener('click', () => this._addToCart());
    }

    // Stepper nav — allow jumping back to completed steps
    this.stepperItems.forEach((item, i) => {
      item.addEventListener('click', () => {
        if (i < this.currentStep || this._isStepComplete(i)) {
          this._goToStep(i);
        }
      });
    });
  }

  // ─── Product selection ──────────────────────────────────────

  _handleProductClick(card) {
    if (card.dataset.unavailable === 'true') return;

    const stepIndex = parseInt(card.dataset.step, 10);
    const isMulti   = card.dataset.multi === 'true';
    const variantId = this._getActiveVariantId(card);
    const productId = card.dataset.productId;
    const price     = this._getActivePrice(card);
    const name      = card.dataset.name;

    if (!this.selections[stepIndex]) this.selections[stepIndex] = [];

    if (isMulti) {
      const existing = this.selections[stepIndex].findIndex(s => s.variantId === variantId);
      if (existing > -1) {
        this.selections[stepIndex].splice(existing, 1);
        card.classList.remove('is-selected');
        card.setAttribute('aria-checked', 'false');
      } else {
        this.selections[stepIndex].push({ variantId, productId, price, name });
        card.classList.add('is-selected');
        card.setAttribute('aria-checked', 'true');
      }
    } else {
      // Single select — clear others in this step
      this.container.querySelectorAll(`.pb-product[data-step="${stepIndex}"]`).forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      this.selections[stepIndex] = [{ variantId, productId, price, name }];
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
    }

    this._updateNextBtn();
    this._updateSummary();
    this._checkBundle();
  }

  _getActiveVariantId(card) {
    const select = card.querySelector('[data-product-card-variant]');
    return select ? select.value : card.dataset.variantId;
  }

  _getActivePrice(card) {
    const select = card.querySelector('[data-product-card-variant]');
    if (select) {
      const opt = select.options[select.selectedIndex];
      return parseInt(opt.dataset.price, 10);
    }
    return parseInt(card.dataset.price, 10);
  }

  _updateCardVariant(card, select) {
    const stepIndex  = parseInt(card.dataset.step, 10);
    const newId      = select.value;
    const opt        = select.options[select.selectedIndex];
    const newPrice   = parseInt(opt.dataset.price, 10);
    const available  = opt.dataset.available !== 'false';

    if (!available) return; // shouldn't happen (disabled option) but guard anyway

    const sel = this.selections[stepIndex];
    if (!sel) return;

    // Find item by the OLD variantId stored on the card element
    const oldId = card.dataset.variantId;
    const item  = sel.find(s => s.variantId === oldId);
    if (item) {
      item.variantId = newId;
      item.price     = newPrice;
    }

    // Update the card's own data attr so subsequent lookups are correct
    card.dataset.variantId = newId;
    card.dataset.price     = newPrice;

    // Update displayed price
    const priceEl = card.querySelector('.pb-product__price-amount');
    if (priceEl) priceEl.textContent = this._formatMoney(newPrice);

    this._updateSummary();
  }

  // ─── Step navigation ────────────────────────────────────────

  _goNext() {
    if (this.currentStep < this.totalSteps - 1) {
      this._goToStep(this.currentStep + 1);
    }
  }

  _goPrev() {
    if (this.currentStep > 0) {
      this._goToStep(this.currentStep - 1);
    }
  }

  _goToStep(index) {
    this.stepPanels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });

    this.stepperItems.forEach((item, i) => {
      item.classList.remove('is-active', 'is-done');
      item.removeAttribute('aria-current');
      if (i === index) {
        item.classList.add('is-active');
        item.setAttribute('aria-current', 'step');
      } else if (i < index || this._isStepComplete(i)) {
        item.classList.add('is-done');
      }
    });

    this.currentStep = index;
    this._updateNextBtn();

    // Smooth scroll to section top (accounts for sticky headers)
    const offset = 80;
    const top    = this.container.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  _isStepComplete(index) {
    const step = this.steps[index];
    if (!step.required) return true;
    return Array.isArray(this.selections[index]) && this.selections[index].length > 0;
  }

  _allRequiredComplete() {
    return this.steps.every((_, i) => this._isStepComplete(i));
  }

  _updateNextBtn() {
    const panel = this.stepPanels[this.currentStep];
    if (!panel) return;

    const nextBtn = panel.querySelector('[data-action="next"]');
    if (nextBtn) {
      nextBtn.disabled = !this._isStepComplete(this.currentStep);
    }
  }

  // ─── Summary sidebar ────────────────────────────────────────

  _updateSummary() {
    const items = [];

    this.steps.forEach((step, i) => {
      const stepSels = this.selections[i];
      if (stepSels && stepSels.length > 0) {
        stepSels.forEach(sel => {
          items.push({ stepTitle: step.title, ...sel });
        });
      }
    });

    if (items.length === 0) {
      this.summaryList.innerHTML = '<p class="pb-summary__empty">No items selected yet</p>';
      if (this.summaryFooter) this.summaryFooter.hidden = true;
      return;
    }

    this.summaryList.innerHTML = items.map(item => `
      <div class="pb-summary__item">
        <div class="pb-summary__item-info">
          <p class="pb-summary__item-step">${this._escape(item.stepTitle)}</p>
          <p class="pb-summary__item-name">${this._escape(item.name)}</p>
        </div>
        <span class="pb-summary__item-price">${this._formatMoney(item.price)}</span>
      </div>
    `).join('');

    const total = items.reduce((sum, item) => sum + item.price, 0);

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this._formatMoney(total);
    }

    if (this.summaryFooter) {
      this.summaryFooter.hidden = false;
    }

    const canCheckout = this._allRequiredComplete();

    if (this.sidebarBtn) {
      this.sidebarBtn.disabled = !canCheckout;
    }

    if (this.sidebarNote) {
      this.sidebarNote.classList.toggle('is-hidden', canCheckout);
    }
  }

  // ─── Add to cart ────────────────────────────────────────────

  // ─── Bundle discount detection ──────────────────────────────

  /**
   * Checks whether every product in the configured bundle is currently
   * selected (matched by PRODUCT id, so any variant/size qualifies).
   * Shows/hides the bundle banner and toggles this.bundleQualified, which
   * _addToCart uses to apply the discount code at checkout redirect.
   */
  _checkBundle() {
    if (!this.bundle || !Array.isArray(this.bundle.productIds) || this.bundle.productIds.length === 0) {
      return;
    }

    // Collect all selected product IDs across every step (as strings)
    const selectedProductIds = new Set();
    Object.values(this.selections).forEach(stepSels => {
      stepSels.forEach(sel => {
        if (sel.productId != null) selectedProductIds.add(String(sel.productId));
      });
    });

    // Bundle qualifies only when ALL configured products are present
    const qualifies = this.bundle.productIds.every(id => selectedProductIds.has(String(id)));

    this.bundleQualified = qualifies;

    if (this.bundleBanner) {
      if (qualifies) {
        const amount = (this.bundle.amount || '').trim();
        let msg = (this.bundle.message || '').trim();
        if (!msg) {
          // No custom message configured — build a sensible default
          msg = amount ? `Bundle deal — ${amount} off applied at checkout`
                       : 'Bundle deal applied at checkout';
        } else {
          msg = msg.replace('{amount}', amount);
        }
        // Collapse any double spaces left by an empty {amount}
        msg = msg.replace(/\s{2,}/g, ' ').trim();
        this.bundleBanner.textContent = msg;
        this.bundleBanner.hidden = false;
      } else {
        this.bundleBanner.hidden = true;
      }
    }
  }

  async _addToCart() {
    if (!this._allRequiredComplete()) {
      // Find first incomplete required step and jump to it
      const firstIncomplete = this.steps.findIndex((step, i) => step.required && !this._isStepComplete(i));
      if (firstIncomplete > -1) {
        this._goToStep(firstIncomplete);
      }
      return;
    }

    const packageId = `pkg-${Date.now()}`;
    const items     = [];

    this.steps.forEach((step, i) => {
      const stepSels = this.selections[i];
      if (!stepSels) return;
      stepSels.forEach(sel => {
        items.push({
          id:         parseInt(sel.variantId, 10),
          quantity:   1,
          properties: {
            _package_id:   packageId,
            _package_step: step.title
          }
        });
      });
    });

    if (items.length === 0) return;

    this._setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'There was a problem adding your package to the cart.');
      }

      // Update Broadcast's cart count bubble if present
      this._refreshCartCount();

      // If the bundle qualifies and a discount code is configured, apply it
      // via Shopify's discount URL, which sets it on the cart session and
      // then redirects. Works on every Shopify plan.
      if (this.bundleQualified && this.bundle && this.bundle.code) {
        const code = encodeURIComponent(this.bundle.code);
        window.location.href = `/discount/${code}?redirect=/cart`;
      } else {
        window.location.href = '/cart';
      }

    } catch (err) {
      this._setLoading(false);
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }

  // ─── Helpers ────────────────────────────────────────────────

  _setLoading(visible) {
    if (this.loadingEl) this.loadingEl.hidden = !visible;
  }

  _showError(message) {
    // Use Broadcast's notification system if available, otherwise alert
    if (window.theme && window.theme.showError) {
      window.theme.showError(message);
    } else {
      alert(message);
    }
  }

  /**
   * Nudge Broadcast's header cart bubble.
   * Broadcast listens for the native `cart:refresh` event dispatched
   * from its own cart drawer / fetch helpers. We fire the same event
   * so the count stays in sync without a full page reload.
   */
  _refreshCartCount() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Also try fetching /cart.js and updating the count directly
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const countEls = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          el.setAttribute('data-cart-count', cart.item_count);
        });
      })
      .catch(() => { /* non-critical */ });
  }

  /**
   * Money formatter.
   * Uses Shopify's moneyFormat string from {{ shop.money_format }} passed
   * via data-money-format on the section wrapper. Falls back to the
   * browser Intl API if the format string is absent.
   */
  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);

    if (this.moneyFormat && this.moneyFormat.includes('{{')) {
      // Process most-specific patterns FIRST so {{amount}} doesn't corrupt
      // {{amount_no_decimals}} or {{amount_with_comma_separator}} strings.
      return this.moneyFormat
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}',             amount.replace('.', ','))
        .replace('{{amount_no_decimals}}',                      Math.round(cents / 100))
        .replace('{{amount}}',                                  amount);
    }

    // Fallback — detect currency from Shopify global if present
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    return new Intl.NumberFormat('en-CA', {
      style:                 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(cents / 100);
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// ─── Init ───────────────────────────────────────────────────────

function initPackageBuilders() {
  document.querySelectorAll('.package-builder[data-section-id]').forEach(el => {
    if (!el._pbInstance) {
      el._pbInstance = new PackageBuilder(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPackageBuilders);
} else {
  initPackageBuilders();
}

// Shopify Theme Editor live-reload support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.package-builder[data-section-id]');
    if (el) el._pbInstance = new PackageBuilder(el);
  });

  document.addEventListener('shopify:section:unload', e => {
    const el = e.target.querySelector('.package-builder[data-section-id]');
    if (el) el._pbInstance = null;
  });

  // Re-init on block add/remove/select so the editor stays interactive
  ['shopify:block:select', 'shopify:block:deselect'].forEach(evt => {
    document.addEventListener(evt, e => {
      const el = e.target.closest('.package-builder[data-section-id]');
      if (el && el._pbInstance) {
        el._pbInstance._updateSummary();
      }
    });
  });
}